(ns yardwerkz.web.store
  "RFX state management for the web customer app.
   Stripped-down version of the mobile store — customer-only, no worker state."
  (:require [io.factorhouse.rfx.core :as rfx]
            [yardwerkz.web.effects :as effects]
            [yardwerkz.web.domain.service :as service]
            [lambdaisland.glogi :as log]))

;; =============================================================================
;; localStorage Persistence (replaces MMKV)
;; =============================================================================

(def ^:private draft-key "yw.draft")

(defn- persist-draft! [draft]
  (try
    (if draft
      (js/localStorage.setItem draft-key (js/JSON.stringify (clj->js draft)))
      (js/localStorage.removeItem draft-key))
    (log/debug :store/persisted {:has-draft (some? draft)})
    (catch js/Error e
      (log/warn :store/persist-failed {:error (.-message e)}))))

(defn load-draft []
  (try
    (when-let [json (js/localStorage.getItem draft-key)]
      (js->clj (js/JSON.parse json) :keywordize-keys true))
    (catch js/Error e
      (log/warn :store/load-failed {:error (.-message e)})
      nil)))

;; =============================================================================
;; RFX Context — Customer Only
;; =============================================================================

(defonce rfx-ctx
  (rfx/init {:initial-value {:draft-job nil
                             :identity nil
                             :auth-ready false
                             ;; Services catalog
                             :services nil
                             :services-loading false
                             :services-error nil
                             ;; Saved addresses (properties)
                             :saved-addresses nil
                             :saved-addresses-loading false
                             :saved-addresses-error nil
                             ;; Jobs list
                             :jobs nil
                             :jobs-loading false
                             :jobs-error nil
                             ;; Current job detail
                             :current-job nil
                             :current-job-loading false
                             :current-job-error nil
                             :job-cancelling false
                             ;; Notifications
                             :notifications nil
                             :notifications-loading false
                             :notifications-error nil
                             :unread-count 0
                             :notification-marking-read false
                             ;; Stripe
                             :stripe-status nil
                             :stripe-status-loading false
                             ;; Booking flow
                             :area-loading false
                             :area-result nil
                             :job-submitting false
                             :job-submit-error nil
                             ;; Quotes
                             :quotes {}
                             ;; Notification preferences
                             :notification-preferences nil
                             ;; Generic realtime subscriptions
                             :subscriptions {}
                             ;; Conversations
                             :conversations nil
                             :conversations-loading false}}))

;; =============================================================================
;; Effects
;; =============================================================================

(rfx/reg-fx :persist
            (fn [_rfx-ctx draft]
              (persist-draft! draft)))

(effects/register-effects! rfx/reg-fx)

;; =============================================================================
;; Draft Job Shape (same as mobile)
;; =============================================================================
;;
;; {:service {:id "mowing" :name "Lawn Mowing" :icon "grass" :colors {...}}
;;  :address {:address "123 Oak St" :lng -89.6501 :lat 39.7817
;;            :city "Springfield" :state "IL" :postal-code "62701"}
;;  :geo [{:id "uuid" :coords [[lng lat]...] :tag "front"} ...]
;;  :details {:has-pets false :terrain "flat"
;;            :access-instructions "" :photos []}
;;  :schedule {:date #inst "..." :time "morning"}
;;  :pricing {:tier "standard" :custom-price nil :display-price "$60-75"}}

;; =============================================================================
;; Generic Realtime Subscriptions
;; =============================================================================

(rfx/reg-event-fx ::realtime-subscribe
                  (fn [{:keys [db]} [_ {:keys [sub-id type id]}]]
                    (let [existing (get-in db [:subscriptions sub-id])]
                      {:db (assoc-in db [:subscriptions sub-id]
                                     (if existing
                                       (assoc existing :loading? true :error nil)
                                       {:data nil :loading? true :error nil}))
                       :realtime/subscribe {:sub-id sub-id :type type :id id
                                            :success-event [::realtime-data-received sub-id]
                                            :on-update-event [::realtime-data-received sub-id]}})))

(rfx/reg-event-db ::realtime-data-received
                  (fn [db [_ sub-id data]]
                    (assoc-in db [:subscriptions sub-id] {:data data :loading? false :error nil})))

(rfx/reg-event-fx ::realtime-unsubscribe
                  (fn [{:keys [db]} [_ sub-id]]
                    {:db (update db :subscriptions dissoc sub-id)
                     :realtime/unsubscribe {:sub-id sub-id}}))

(rfx/reg-sub ::subscription-data
             (fn [db [_ sub-id]]
               (get-in db [:subscriptions sub-id])))

;; =============================================================================
;; Draft Events
;; =============================================================================

(rfx/reg-event-db ::hydrate-draft
                  (fn [db [_ draft]]
                    (assoc db :draft-job draft)))

(rfx/reg-event-fx ::reset-draft
                  (fn [{:keys [db]} _]
                    {:db (assoc db :draft-job nil)
                     :persist nil}))

(rfx/reg-event-fx ::set-service
                  (fn [{:keys [db]} [_ service]]
                    (let [new-db (assoc-in db [:draft-job :service] service)]
                      {:db new-db :persist (:draft-job new-db)})))

(rfx/reg-event-fx ::set-address
                  (fn [{:keys [db]} [_ address]]
                    (let [new-db (assoc-in db [:draft-job :address] address)]
                      {:db new-db :persist (:draft-job new-db)})))

(rfx/reg-event-fx ::set-geo
                  (fn [{:keys [db]} [_ geo]]
                    (let [new-db (assoc-in db [:draft-job :geo] geo)]
                      {:db new-db :persist (:draft-job new-db)})))

(rfx/reg-event-fx ::set-details
                  (fn [{:keys [db]} [_ details]]
                    (let [new-db (assoc-in db [:draft-job :details] details)]
                      {:db new-db :persist (:draft-job new-db)})))

(rfx/reg-event-fx ::set-schedule
                  (fn [{:keys [db]} [_ schedule]]
                    (let [new-db (assoc-in db [:draft-job :schedule] schedule)]
                      {:db new-db :persist (:draft-job new-db)})))

(rfx/reg-event-fx ::set-pricing
                  (fn [{:keys [db]} [_ pricing]]
                    (let [new-db (assoc-in db [:draft-job :pricing] pricing)]
                      {:db new-db :persist (:draft-job new-db)})))

;; =============================================================================
;; Identity
;; =============================================================================

(rfx/reg-event-fx ::set-identity
                  (fn [{:keys [db]} [_ identity]]
                    (let [account-id (:id identity)]
                      {:db (assoc db :identity identity :auth-ready true)
                       :fx (when account-id
                             [[:realtime/subscribe
                               {:sub-id (str "unread-count:" account-id)
                                :type :topic/unread-count
                                :id account-id
                                :success-event [::unread-count-loaded]
                                :on-update-event [::unread-count-loaded]}]])})))

(rfx/reg-event-db ::clear-identity
                  (fn [db _]
                    (assoc db :identity nil :auth-ready false)))

(rfx/reg-event-db ::unread-count-loaded
                  (fn [db [_ data]]
                    (assoc db :unread-count (or (:count data) data 0))))

;; =============================================================================
;; Subscriptions
;; =============================================================================

(rfx/reg-sub ::draft-job (fn [db _] (:draft-job db)))
(rfx/reg-sub ::draft-service (fn [db _] (get-in db [:draft-job :service])))
(rfx/reg-sub ::draft-address (fn [db _] (get-in db [:draft-job :address])))
(rfx/reg-sub ::draft-geo (fn [db _] (get-in db [:draft-job :geo])))
(rfx/reg-sub ::draft-details (fn [db _] (get-in db [:draft-job :details])))
(rfx/reg-sub ::draft-schedule (fn [db _] (get-in db [:draft-job :schedule])))
(rfx/reg-sub ::draft-pricing (fn [db _] (get-in db [:draft-job :pricing])))

(rfx/reg-sub ::identity (fn [db _] (:identity db)))
(rfx/reg-sub ::auth-ready (fn [db _] (boolean (:auth-ready db))))
(rfx/reg-sub ::customer-id (fn [db _] (get-in db [:identity :customer-id])))
(rfx/reg-sub ::unread-count (fn [db _] (:unread-count db)))

(rfx/reg-sub ::services (fn [db _] (:services db)))
(rfx/reg-sub ::services-loading (fn [db _] (:services-loading db)))

(rfx/reg-sub ::saved-addresses (fn [db _] (:saved-addresses db)))
(rfx/reg-sub ::saved-addresses-loading (fn [db _] (:saved-addresses-loading db)))

(rfx/reg-sub ::jobs (fn [db _] (:jobs db)))
(rfx/reg-sub ::jobs-loading (fn [db _] (:jobs-loading db)))

(rfx/reg-sub ::current-job (fn [db _] (:current-job db)))
(rfx/reg-sub ::current-job-loading (fn [db _] (:current-job-loading db)))

(rfx/reg-sub ::job-submitting (fn [db _] (:job-submitting db)))
(rfx/reg-sub ::job-submit-error (fn [db _] (:job-submit-error db)))

(rfx/reg-sub ::area-result (fn [db _] (:area-result db)))
(rfx/reg-sub ::area-loading (fn [db _] (:area-loading db)))

(rfx/reg-sub ::stripe-status (fn [db _] (:stripe-status db)))

(rfx/reg-sub ::notifications (fn [db _] (:notifications db)))
(rfx/reg-sub ::notification-preferences (fn [db _] (:notification-preferences db)))

(rfx/reg-sub ::conversations (fn [db _] (:conversations db)))
(rfx/reg-sub ::conversations-loading (fn [db _] (:conversations-loading db)))

;; =============================================================================
;; Area Calculation
;; =============================================================================

(rfx/reg-event-fx ::calculate-area
                  (fn [{:keys [db]} [_ geo]]
                    (if (seq geo)
                      {:db (assoc db :area-loading true)
                       :http/batch-request {:action :geo/calculate-area
                                            :data {:zones geo}
                                            :success-event [::calculate-area-success]
                                            :failure-event [::calculate-area-failure]}}
                      {:db (-> db (dissoc :area-result) (assoc :area-loading false))})))

(rfx/reg-event-db ::calculate-area-success
                  (fn [db [_ result]]
                    (-> db (assoc :area-loading false) (assoc :area-result (:data result)))))

(rfx/reg-event-db ::calculate-area-failure
                  (fn [db [_ _error]]
                    (assoc db :area-loading false)))

;; =============================================================================
;; Services Fetch
;; =============================================================================

(rfx/reg-event-fx ::fetch-services
                  (fn [{:keys [db]} [_]]
                    {:db (assoc db :services-loading true)
                     :http/batch-request {:action :service/list
                                          :data {}
                                          :success-event [::fetch-services-success]
                                          :failure-event [::fetch-services-failure]}}))

(rfx/reg-event-db ::fetch-services-success
                  (fn [db [_ result]]
                    (-> db
                        (assoc :services-loading false)
                        (assoc :services (service/enrich-services (get-in result [:data :services])))
                        (dissoc :services-error))))

(rfx/reg-event-db ::fetch-services-failure
                  (fn [db [_ error]]
                    (-> db
                        (assoc :services-loading false)
                        (assoc :services-error (:message error "Failed to load services")))))

;; =============================================================================
;; Saved Addresses
;; =============================================================================

(rfx/reg-event-fx ::fetch-saved-addresses
                  (fn [{:keys [db]} [_ {:keys [customer-id]}]]
                    (if customer-id
                      {:db (assoc db :saved-addresses-loading true)
                       :http/batch-request {:action :property/list
                                            :data {:customer-id customer-id}
                                            :success-event [::fetch-saved-addresses-success]
                                            :failure-event [::fetch-saved-addresses-failure]}}
                      {})))

(rfx/reg-event-db ::fetch-saved-addresses-success
                  (fn [db [_ result]]
                    (-> db
                        (assoc :saved-addresses-loading false)
                        (assoc :saved-addresses (:data result))
                        (dissoc :saved-addresses-error))))

(rfx/reg-event-db ::fetch-saved-addresses-failure
                  (fn [db [_ error]]
                    (-> db
                        (assoc :saved-addresses-loading false)
                        (assoc :saved-addresses-error (:message error "Failed to load addresses")))))

;; =============================================================================
;; Jobs List
;; =============================================================================

(rfx/reg-event-fx ::fetch-jobs
                  (fn [{:keys [db]} [_ {:keys [customer-id]}]]
                    (if customer-id
                      {:db (assoc db :jobs-loading true)
                       :http/batch-request {:action :job/list-customer
                                            :data {:customer-id customer-id}
                                            :success-event [::fetch-jobs-success]
                                            :failure-event [::fetch-jobs-failure]}}
                      {})))

(rfx/reg-event-db ::fetch-jobs-success
                  (fn [db [_ result]]
                    (-> db
                        (assoc :jobs-loading false)
                        (assoc :jobs (:data result))
                        (dissoc :jobs-error))))

(rfx/reg-event-db ::fetch-jobs-failure
                  (fn [db [_ error]]
                    (-> db
                        (assoc :jobs-loading false)
                        (assoc :jobs-error (:message error "Failed to load jobs")))))

;; =============================================================================
;; Job Submission
;; =============================================================================

(rfx/reg-event-fx ::submit-job
                  (fn [{:keys [db]} [_ {:keys [on-success on-failure]}]]
                    (let [draft (:draft-job db)
                          customer-id (get-in db [:identity :customer-id])
                          job-data {:customer-id customer-id
                                    :service-id (get-in draft [:service :id])
                                    :service-name (get-in draft [:service :name])
                                    :address (get-in draft [:address :address])
                                    :address-data (:address draft)
                                    :geo (:geo draft)
                                    :details (:details draft)
                                    :schedule (:schedule draft)
                                    :pricing (:pricing draft)}]
                      {:db (-> db
                               (assoc :job-submitting true)
                               (dissoc :job-submit-error))
                       :http/batch-request {:action :job/create
                                            :data job-data
                                            :success-event [::submit-job-success]
                                            :failure-event [::submit-job-failure]
                                            :on-success (fn [result]
                                                          (when on-success
                                                            (on-success (get-in result [:data :job-id]))))
                                            :on-failure (fn [err]
                                                          (when on-failure (on-failure err)))}})))

(rfx/reg-event-fx ::submit-job-success
                  (fn [{:keys [db]} [_ _result]]
                    {:db (-> db
                             (assoc :job-submitting false)
                             (assoc :draft-job nil))
                     :persist nil}))

(rfx/reg-event-db ::submit-job-failure
                  (fn [db [_ error]]
                    (-> db
                        (assoc :job-submitting false)
                        (assoc :job-submit-error (:message error "Failed to submit job")))))

;; =============================================================================
;; Chat / Messages
;; =============================================================================

(rfx/reg-event-fx ::send-message
                  (fn [_ [_ {:keys [job-id text]}]]
                    {:http/batch-request {:action :message/send
                                          :data {:job-id job-id :text text}}}))

(rfx/reg-event-fx ::fetch-conversations
                  (fn [{:keys [db]} _]
                    {:db (assoc db :conversations-loading true)
                     :http/batch-request {:action :message/list-conversations
                                          :data {}
                                          :success-event [::conversations-loaded]
                                          :failure-event [::conversations-error]}}))

(rfx/reg-event-db ::conversations-loaded
                  (fn [db [_ result]]
                    (-> db
                        (assoc :conversations-loading false)
                        (assoc :conversations (:data result)))))

(rfx/reg-event-db ::conversations-error
                  (fn [db [_ _error]]
                    (assoc db :conversations-loading false)))

;; =============================================================================
;; Quotes
;; =============================================================================

(rfx/reg-event-fx ::fetch-quotes
                  (fn [{:keys [db]} [_ {:keys [job-id]}]]
                    (when job-id
                      {:db (assoc-in db [:quotes job-id :loading] true)
                       :http/batch-request {:action :quote/list
                                            :data {:job-id job-id}
                                            :success-event [::fetch-quotes-success job-id]
                                            :failure-event [::fetch-quotes-failure job-id]}})))

(rfx/reg-event-db ::fetch-quotes-success
                  (fn [db [_ job-id result]]
                    (assoc-in db [:quotes job-id]
                              {:data (get-in result [:data :quotes] (:data result))
                               :loading false})))

(rfx/reg-event-db ::fetch-quotes-failure
                  (fn [db [_ job-id _error]]
                    (assoc-in db [:quotes job-id :loading] false)))

(rfx/reg-sub ::job-quotes
             (fn [db [_ job-id]]
               (get-in db [:quotes job-id])))

(rfx/reg-sub ::job-cancelling (fn [db _] (:job-cancelling db)))

;; =============================================================================
;; Job Actions (accept quote, cancel, approve work, rate, dispute)
;; =============================================================================

(rfx/reg-event-fx ::accept-quote
                  (fn [_ [_ {:keys [quote-id on-success on-failure]}]]
                    {:http/batch-request {:action :quote/accept
                                          :data {:quote-id quote-id}
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-fx ::decline-quote
                  (fn [_ [_ {:keys [quote-id on-success on-failure]}]]
                    {:http/batch-request {:action :quote/decline
                                          :data {:quote-id quote-id}
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-fx ::cancel-job
                  (fn [{:keys [db]} [_ {:keys [job-id reason on-success on-failure]}]]
                    {:db (assoc db :job-cancelling true)
                     :http/batch-request {:action :job/cancel
                                          :data {:job-id job-id :reason reason}
                                          :success-event [::cancel-job-success]
                                          :failure-event [::cancel-job-failure]
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-db ::cancel-job-success
                  (fn [db _] (assoc db :job-cancelling false)))

(rfx/reg-event-db ::cancel-job-failure
                  (fn [db _] (assoc db :job-cancelling false)))

(rfx/reg-event-fx ::approve-work
                  (fn [_ [_ {:keys [job-id on-success on-failure]}]]
                    {:http/batch-request {:action :job/approve-work
                                          :data {:job-id job-id}
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-fx ::rate-job
                  (fn [_ [_ {:keys [job-id rating review on-success on-failure]}]]
                    {:http/batch-request {:action :job/rate
                                          :data {:job-id job-id :rating rating :review review}
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-fx ::dispute-job
                  (fn [_ [_ {:keys [job-id reason on-success on-failure]}]]
                    {:http/batch-request {:action :job/dispute
                                          :data {:job-id job-id :reason reason}
                                          :on-success on-success
                                          :on-failure on-failure}}))

;; =============================================================================
;; Notification Preferences
;; =============================================================================

(rfx/reg-event-fx ::fetch-notification-preferences
                  (fn [_ _]
                    {:http/batch-request {:action :notification/get-preferences
                                          :data {}
                                          :success-event [::notification-preferences-loaded]}}))

(rfx/reg-event-db ::notification-preferences-loaded
                  (fn [db [_ result]]
                    (assoc db :notification-preferences (:data result))))

(rfx/reg-event-fx ::update-notification-preferences
                  (fn [_ [_ prefs]]
                    {:http/batch-request {:action :notification/update-preferences
                                          :data prefs}}))

;; =============================================================================
;; Mark Notifications Read
;; =============================================================================

(rfx/reg-event-fx ::mark-notifications-read
                  (fn [{:keys [db]} [_ notification-ids]]
                    {:db (assoc db :notification-marking-read true)
                     :http/batch-request {:action :notification/mark-read
                                          :data {:notification-ids notification-ids}
                                          :success-event [::mark-read-success]
                                          :failure-event [::mark-read-failure]}}))

(rfx/reg-event-db ::mark-read-success
                  (fn [db _] (assoc db :notification-marking-read false)))

(rfx/reg-event-db ::mark-read-failure
                  (fn [db _] (assoc db :notification-marking-read false)))

;; =============================================================================
;; Payment Methods
;; =============================================================================

(rfx/reg-event-fx ::fetch-payment-methods
                  (fn [_ _]
                    {:http/batch-request {:action :payment/get-payment-methods
                                          :data {}
                                          :success-event [::payment-methods-loaded]}}))

(rfx/reg-event-db ::payment-methods-loaded
                  (fn [db [_ result]]
                    (assoc db :payment-methods (:data result))))

(rfx/reg-sub ::payment-methods (fn [db _] (:payment-methods db)))

(rfx/reg-event-fx ::create-setup-intent
                  (fn [_ [_ {:keys [on-success on-failure]}]]
                    {:http/batch-request {:action :payment/create-setup-intent
                                          :data {}
                                          :on-success on-success
                                          :on-failure on-failure}}))

(rfx/reg-event-fx ::delete-payment-method
                  (fn [_ [_ {:keys [payment-method-id on-success on-failure]}]]
                    {:http/batch-request {:action :payment/delete-payment-method
                                          :data {:payment-method-id payment-method-id}
                                          :on-success on-success
                                          :on-failure on-failure}}))

;; =============================================================================
;; Payment History
;; =============================================================================

(rfx/reg-event-fx ::fetch-payment-history
                  (fn [_ _]
                    {:http/batch-request {:action :payment/history
                                          :data {}
                                          :success-event [::payment-history-loaded]}}))

(rfx/reg-event-db ::payment-history-loaded
                  (fn [db [_ result]]
                    (assoc db :payment-history (:data result))))

(rfx/reg-sub ::payment-history (fn [db _] (:payment-history db)))

;; =============================================================================
;; Profile
;; =============================================================================

(rfx/reg-event-fx ::update-profile
                  (fn [_ [_ {:keys [data on-success on-failure]}]]
                    {:http/batch-request {:action :account/update-profile
                                          :data data
                                          :on-success on-success
                                          :on-failure on-failure}}))

;; =============================================================================
;; Routing
;; =============================================================================

(rfx/reg-event-db ::set-route
                  (fn [db [_ route]]
                    (assoc db :current-route route)))

(rfx/reg-sub ::current-route (fn [db _] (:current-route db)))
